import { sql } from 'drizzle-orm';
import {
  pgTable,
  pgPolicy,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  uuid,
  integer,
  date,
} from "drizzle-orm/pg-core";
import { organizations } from './organizations';
import { users } from './users';
import { claims } from './claims';
import { emailAccounts } from './communications';

export const EMAIL_CLAIM_SCAN_STATUSES = ['scanning', 'complete', 'failed'] as const;
export type EmailClaimScanStatus = typeof EMAIL_CLAIM_SCAN_STATUSES[number];

/** connect: the firm's first Gmail connection. manual: "scan again". sync: a later mailbox sync. */
export const EMAIL_CLAIM_SCAN_TRIGGERS = ['connect', 'manual', 'sync'] as const;
export type EmailClaimScanTrigger = typeof EMAIL_CLAIM_SCAN_TRIGGERS[number];

/** llm: LiteLLM (Fireworks) read the email. rules: LiteLLM was unavailable, so only the deterministic detector ran. */
export const EMAIL_CLAIM_DETECTION_METHODS = ['llm', 'rules'] as const;
export type EmailClaimDetectionMethod = typeof EMAIL_CLAIM_DETECTION_METHODS[number];

/**
 * proposed: waiting for the user. confirmed: the user confirmed it and claim_id is the new Claim.
 * dismissed: the user said it is not a claim. duplicate: the firm already has this Claim (claim_id).
 */
export const EMAIL_CLAIM_CANDIDATE_STATUSES = ['proposed', 'confirmed', 'dismissed', 'duplicate'] as const;
export type EmailClaimCandidateStatus = typeof EMAIL_CLAIM_CANDIDATE_STATUSES[number];

/**
 * One source email behind a proposed claim. The first-claim scan is subject-only (docs/first-claim-from-email.md): it
 * stores the subject and the from/to/date headers, and `bodyExcerpt` is empty (the body is never read or stored). The
 * field is kept for the shape (and for any rows written before this change).
 */
export interface EmailClaimSourceEmail {
  gmailMessageId: string;
  threadId: string | null;
  subject: string | null;
  from: string | null;
  to: string | null;
  date: string | null;
  direction: 'inbound' | 'outbound';
  bodyExcerpt: string;
  /** Files on the email (metadata only; the bytes are downloaded from Gmail when the Claim is created). */
  attachments?: EmailClaimSourceAttachment[];
}

export interface EmailClaimSourceAttachment {
  attachmentId: string;
  filename: string;
  mimeType: string;
  size: number;
}

// One row per connected mailbox: the "scanning inbox / found N / none found" state the first-claim screen reads.
// The firm is recorded at connect time because email_accounts has no org_id (MIS-86).
export const emailClaimScans = pgTable("email_claim_scans", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  emailAccountId: uuid("email_account_id").references(() => emailAccounts.id, { onDelete: "set null" }),
  mailbox: varchar("mailbox"),
  status: varchar("status", { length: 20 }).$type<EmailClaimScanStatus>().notNull().default("scanning"),
  trigger: varchar("trigger", { length: 20 }).$type<EmailClaimScanTrigger>().notNull().default("connect"),
  detectionMethod: varchar("detection_method", { length: 20 }).$type<EmailClaimDetectionMethod>(),
  // How many emails the scan will look at, known once the mailbox search returns; null before, and for a sync row.
  emailsTotal: integer("emails_total"),
  emailsScanned: integer("emails_scanned").notNull().default(0),
  emailsMatched: integer("emails_matched").notNull().default(0),
  candidatesFound: integer("candidates_found").notNull().default(0),
  error: text("error"),
  startedAt: timestamp("started_at").notNull(),
  heartbeatAt: timestamp("heartbeat_at"), // bumped while the scan runs; a scanning row with an old heartbeat is stale (a restart killed it)
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
}, (table) => [
  index("idx_email_claim_scans_org_id").on(table.orgId),
  uniqueIndex("uq_email_claim_scans_org_account").on(table.orgId, table.emailAccountId),
  pgPolicy('email_claim_scans_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();

// A claim found in a firm's email, waiting for one-click confirmation. One row per claim, however many emails mention it.
export const emailClaimCandidates = pgTable("email_claim_candidates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  emailAccountId: uuid("email_account_id").references(() => emailAccounts.id, { onDelete: "set null" }),
  status: varchar("status", { length: 20 }).$type<EmailClaimCandidateStatus>().notNull().default("proposed"),
  // The identity the row is unique by, per firm: claim number, else policy number + date of loss, else address + date of loss.
  dedupeKey: text("dedupe_key").notNull(),
  // Every identity seen for it (a later email can add one), so a second email finds the same row.
  dedupeKeys: text("dedupe_keys").array().notNull().default(sql`'{}'::text[]`),
  sourceMessageIds: text("source_message_ids").array().notNull().default(sql`'{}'::text[]`),
  sourceEmails: jsonb("source_emails").$type<EmailClaimSourceEmail[]>().notNull().default(sql`'[]'::jsonb`),
  claimNumber: varchar("claim_number"),
  policyNumber: varchar("policy_number"),
  carrierName: varchar("carrier_name"),
  adjusterName: varchar("adjuster_name"),
  adjusterEmail: varchar("adjuster_email"),
  dateOfLoss: date("date_of_loss"),
  perilType: varchar("peril_type"),
  policyholderFirstName: varchar("policyholder_first_name"),
  policyholderLastName: varchar("policyholder_last_name"),
  policyholderCompany: varchar("policyholder_company"),
  policyholderEmail: varchar("policyholder_email"),
  propertyAddress: text("property_address"),
  propertyCity: varchar("property_city"),
  propertyState: varchar("property_state"),
  propertyZip: varchar("property_zip"),
  summary: text("summary"),
  confidence: integer("confidence").notNull().default(0),
  detectionMethod: varchar("detection_method", { length: 20 }).$type<EmailClaimDetectionMethod>().notNull().default("rules"),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  resolvedBy: varchar("resolved_by").references(() => users.id, { onDelete: "set null" }),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
}, (table) => [
  index("idx_email_claim_candidates_org_status").on(table.orgId, table.status),
  uniqueIndex("uq_email_claim_candidates_org_dedupe_key").on(table.orgId, table.dedupeKey),
  pgPolicy('email_claim_candidates_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();

export type EmailClaimScan = typeof emailClaimScans.$inferSelect;
export type InsertEmailClaimScan = typeof emailClaimScans.$inferInsert;
export type EmailClaimCandidate = typeof emailClaimCandidates.$inferSelect;
export type InsertEmailClaimCandidate = typeof emailClaimCandidates.$inferInsert;
